// The matrix's reading of its response, without a component: how the columns
// group and fold, how a count is shaded, which Explore selection a head, a
// row name or a cell opens, and the texts around the table.

import {
  CapabilityGrain,
  Matrix,
  MatrixAggregateRow,
  MatrixColumn,
  MatrixColumns,
  MatrixProductRow,
  MatrixRows,
  MatrixStance,
} from "../../../types/analytics";
import { ExportBuild, ExportTable } from "../../../utils/export/table";
import { AnalyticsSelection, selectionOf } from "../analyticsState";
import { nodeSelection } from "../CapabilityMap/CapabilityMapPanel";
import { counted } from "../Treemap/treemapModel";

// The columns of one effect area, which the table groups and may fold
export interface ColumnGroup {
  // The effect area's id; a column of no area has a key of its own
  key: string;
  // The effect area's name; null for a column that stands alone
  name: string | null;
  columns: MatrixColumn[];
}

// The groups of the columns in the order of their first appearance, the
// columns of a group gathered where it first appears. A column with no effect
// area stands alone, in its place.
export const groupColumns = (columns: MatrixColumn[]): ColumnGroup[] => {
  const groups: ColumnGroup[] = [];
  const byKey = new Map<string, ColumnGroup>();
  for (const column of columns) {
    if (!column.group) {
      groups.push({ key: `column:${column.id}`, name: null, columns: [column] });
      continue;
    }
    const known = byKey.get(column.group.id);
    if (known) {
      known.columns.push(column);
      continue;
    }
    const group: ColumnGroup = {
      key: column.group.id,
      name: column.group.name ?? column.group.id,
      columns: [column],
    };
    byKey.set(column.group.id, group);
    groups.push(group);
  }
  return groups;
};

// What a column is called: the name of what it stands for
export const columnName = (column: MatrixColumn): string =>
  column.entity.name ?? column.entity.id;

// What a row is called
export const rowName = (row: MatrixAggregateRow): string =>
  row.label ?? row.entity?.name ?? row.id;

export interface AxisAbility {
  id: string;
  name: string;
}

// The abilities of a table's capability axis, each once, in its order: the
// abilities of its capability columns (a finer grain draws one ability over
// several), or its capability rows
export const axisAbilities = (matrix: Matrix): AxisAbility[] => {
  const found = new Map<string, string>();
  if (matrix.columns === "capabilities") {
    for (const column of matrix.shown_columns) {
      if (!found.has(column.entity.id)) {
        found.set(column.entity.id, columnName(column));
      }
    }
  } else {
    for (const row of matrix.aggregate_rows) {
      if (!row.other && !found.has(row.id)) {
        found.set(row.id, rowName(row));
      }
    }
  }
  return Array.from(found, ([id, name]) => ({ id, name }));
};

const STANCE_STRENGTH: Record<MatrixStance, number> = { has: 3, requires: 2, lacks: 1 };

// The stance a product row shows for a folded group: the strongest of any of
// its columns (has, then requires, then lacks); null where it has no cell
// in the group
export const strongestStance = (
  row: MatrixProductRow,
  columns: MatrixColumn[],
): MatrixStance | null => {
  let strongest: MatrixStance | null = null;
  for (const column of columns) {
    const stance = row.cells[column.id]?.stance;
    if (stance && (!strongest || STANCE_STRENGTH[stance] > STANCE_STRENGTH[strongest])) {
      strongest = stance;
    }
  }
  return strongest;
};

// Whether an aggregate row counts a product in any column of a folded group.
// The read returns no count for the union, so the folded cell shows a mark
// and no number.
export const anyCountedIn = (row: MatrixAggregateRow, columns: MatrixColumn[]): boolean =>
  columns.some((column) => (row.cells[column.id]?.count ?? 0) > 0);

// The five shades of a count by its share of the column, from the first
// (under a fifth) to the fifth (four fifths and over); 0 for no count
export const SHADE_STEPS = 5;
const SHADE_THRESHOLDS: readonly number[] = [0.2, 0.4, 0.6, 0.8];

export const shadeStep = (count: number, columnCount: number): number => {
  if (count <= 0) {
    return 0;
  }
  if (columnCount <= 0) {
    return 1;
  }
  const share = count / columnCount;
  return 1 + SHADE_THRESHOLDS.filter((threshold) => share >= threshold).length;
};

// The selection with a pick of one kind put in place of the selection's own
const withPick = (
  selection: AnalyticsSelection,
  kind: "category" | "maker" | "capability" | "condition" | "mode",
  id: string,
): AnalyticsSelection => ({ ...selectionOf(selection), [kind]: [id] });

// The selection that opens a column's products in Explore: the page's
// selection plus the column's picks. A capability column adds its capability
// and, at a finer grain, its qualifier; a condition or a mode column adds
// its condition or mode.
export const columnSelection = (
  selection: AnalyticsSelection,
  column: MatrixColumn,
  columns: MatrixColumns,
  grain: CapabilityGrain,
): AnalyticsSelection => {
  if (columns === "conditions") {
    return withPick(selection, "condition", column.entity.id);
  }
  if (columns === "modes") {
    return withPick(selection, "mode", column.entity.id);
  }
  return nodeSelection(
    selection,
    { capability: column.entity, qualifier: column.qualifier },
    grain,
  );
};

// The selection that opens a row's products in Explore; null for the row of
// the products under no class or with no maker, which has no pick
export const rowSelection = (
  selection: AnalyticsSelection,
  row: MatrixAggregateRow,
  rows: MatrixRows,
): AnalyticsSelection | null => {
  if (row.other || !row.entity) {
    return null;
  }
  if (rows === "categories") {
    return withPick(selection, "category", row.entity.id);
  }
  if (rows === "makers") {
    return withPick(selection, "maker", row.entity.id);
  }
  if (rows === "capabilities") {
    return withPick(selection, "capability", row.entity.id);
  }
  return null;
};

// The selection that opens the products of an aggregate cell in Explore: the
// row's picks and the column's
export const cellSelection = (
  selection: AnalyticsSelection,
  row: MatrixAggregateRow,
  column: MatrixColumn,
  matrix: Pick<Matrix, "rows" | "columns" | "grain">,
): AnalyticsSelection | null => {
  const picked = rowSelection(selection, row, matrix.rows);
  return picked && columnSelection(picked, column, matrix.columns, matrix.grain);
};

const COLUMN_NOUN: Record<MatrixColumns, string> = {
  capabilities: "capabilities",
  conditions: "conditions",
  modes: "modes",
};

// What the rows of the table are, and what a cell counts, in a sentence
const rowsSentence = (matrix: Matrix): string => {
  switch (matrix.rows) {
    case "products":
      return "Products in scope with a claim in a column shown";
    case "categories":
      return `Classes at the ${matrix.rank} rank, each counting its products in scope; a product with no class at that rank counts under the nearest class above it; the products under no class come last`;
    case "makers":
      return "Makers of the products in scope, each counting its products, and the products with no maker on record";
    case "capabilities":
      return matrix.columns === "modes"
        ? "Capabilities of the products in scope, each cell counting the products said to have the capability in the mode"
        : "Capabilities of the products in scope, each cell counting the products said to have the capability under the condition";
  }
};

// What the table lists and leaves out, the floors in force and the promise
// that narrowing the rows leaves the column counts alone
export const captionOf = (matrix: Matrix): string => {
  const parts = [rowsSentence(matrix)];
  if (matrix.rows === "products") {
    parts.push(`${matrix.unlisted} of ${matrix.evidence.n} not listed`);
  } else if (matrix.omitted_rows > 0) {
    parts.push(
      `${matrix.omitted_rows} more not listed: at most ${matrix.floors.top_n} rows are shown`,
    );
  }
  parts.push(
    `${matrix.shown_columns.length} of ${matrix.shown_columns.length + matrix.omitted_columns} ${COLUMN_NOUN[matrix.columns]} as columns`,
  );
  parts.push(`a count under ${counted(matrix.floors.min_support, "product")} is hatched`);
  if (matrix.thin_scope) {
    parts.push(
      `the scope holds under ${counted(matrix.floors.min_scope, "product")}: every number is a hint`,
    );
  }
  parts.push("a column head counts the products in the whole scope");
  return `${parts.join(" · ")} · narrowing the rows never changes the column counts.`;
};

// The rows of a page out of the rows after narrowing: "rows 1–5 of 5". The
// pinned row is no part of either number.
export const pagerText = (matrix: Matrix): string => {
  const shown = (
    matrix.rows === "products"
      ? matrix.product_rows.filter((row) => !row.pinned)
      : matrix.aggregate_rows.filter((row) => !row.pinned)
  ).length;
  if (shown === 0) {
    return "no rows";
  }
  return `rows ${matrix.offset + 1}–${matrix.offset + shown} of ${matrix.total}`;
};

const ROW_LABEL: Record<MatrixRows, string> = {
  products: "product",
  categories: "category",
  makers: "maker",
  capabilities: "capability",
};

// The rows shown as an export table: the row's name, a cell for each column
// shown (the stance of a product, the count of an aggregate row, empty for
// none), then the figure at the row's end
export const tableOfMatrix = (matrix: Matrix): ExportTable => {
  const counts = matrix.rows !== "products";
  const cellKind = counts ? "number" : "text";
  return {
    name: `${ROW_LABEL[matrix.rows]} × ${matrix.columns}`,
    columns: [
      { header: ROW_LABEL[matrix.rows], width: 36 },
      ...matrix.shown_columns.map((column) => ({
        header: column.qualifier
          ? `${columnName(column)} · ${column.qualifier.name ?? column.qualifier.id}`
          : columnName(column),
        kind: cellKind as "text" | "number",
      })),
      { header: counts ? "products" : "claims", kind: "number" as const },
    ],
    rows:
      matrix.rows === "products"
        ? matrix.product_rows.map((row) => [
            row.product.name ?? row.product.id,
            ...matrix.shown_columns.map((column) => row.cells[column.id]?.stance ?? ""),
            row.claim_count,
          ])
        : matrix.aggregate_rows.map((row) => [
            rowName(row),
            ...matrix.shown_columns.map((column) => row.cells[column.id]?.count ?? null),
            row.product_count,
          ]),
  };
};

// A file of the rows shown: the table, and in its About sheet the scope it
// was read over, how the rows and the columns were asked for, and the page
export const matrixExport = (
  matrix: Matrix,
  find: string,
  scope: ReadonlyArray<readonly [string, string]> = [],
): ExportBuild => ({
  tables: [tableOfMatrix(matrix)],
  meta: {
    title: `Matrix: ${matrix.rows} × ${matrix.columns}`,
    generatedAt: new Date(),
    filters: [
      ...scope,
      ["Rows", matrix.rows],
      ["Columns", matrix.columns],
      ...(matrix.rows === "categories" ? [["Rank", String(matrix.rank)] as const] : []),
      ...(find.trim() !== "" ? [["Find", find.trim()] as const] : []),
      ["Listed", pagerText(matrix)],
    ],
    note: "Only the rows shown are exported.",
  },
  filename: ["matrix", matrix.rows, matrix.columns],
});
